import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookings } from '@repletoryreach/sdk-api';
import { useDebounce } from '@repletoryreach/sdk-utils/hooks';
import { Button, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, DataTable, EmptyState, PageHeader, StatusBadge, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { Select, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import type { Indexed } from '../../../lib/types';
import { BookingDrawer } from '../components/BookingDrawer';
import { NewBookingModal } from '../components/NewBookingModal';
import {
  BOOKINGS_KEY, bookingErrorText, serviceColour, statusLabel, statusTone, useDateLocale, useOrgTimezone, useServices, useStaff
} from '../lib/data';
import { addDays, formatInZone, todayIn, zonedIso } from '../lib/time';
import '../bookings.css';

const PAGE_SIZE = 50;

export default function BookingsListPage() {
  const t = useT();
  const qc = useQueryClient();
  const tz = useOrgTimezone();
  const locale = useDateLocale();
  const [params, setParams] = useSearchParams();
  const servicesQ = useServices(true);
  const staffQ = useStaff();
  const services = servicesQ.data?.data ?? [];
  const staff = staffQ.data?.data ?? [];

  const [status, setStatus] = useState(params.get('status') ?? '');
  const [serviceId, setServiceId] = useState('');
  const [staffId, setStaffId] = useState('');
  const [from, setFrom] = useState(() => todayIn(tz));
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const q = useDebounce(search, 250);
  const openId = params.get('booking');

  const filters = useMemo(() => ({
    status: status || undefined,
    service_id: serviceId || undefined,
    staff_id: staffId || undefined,
    from: from ? zonedIso(from, '00:00', tz) : undefined,
    to: to ? zonedIso(addDays(to, 1), '00:00', tz) : undefined,
    q: q.trim() || undefined
  }), [status, serviceId, staffId, from, to, q, tz]);
  const filtered = !!(status || serviceId || staffId || to || q.trim());

  const list = useInfiniteQuery({
    queryKey: [BOOKINGS_KEY, 'list', filters],
    queryFn: ({ pageParam }) => bookings.listBookings({ ...filters, limit: PAGE_SIZE, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined
  });
  const rows = (list.data?.pages.flatMap((p) => p.data) ?? []) as Indexed<bookings.Booking>[];

  const colourOf = useMemo(() => new Map(services.map((s) => [s.id, serviceColour(s.colour)])), [services]);

  const refresh = () => qc.invalidateQueries({ queryKey: [BOOKINGS_KEY] });
  const approve = useMutation({
    mutationFn: (b: bookings.Booking) => bookings.approveBooking(b.id),
    onSuccess: (res) => { void refresh(); notifySuccess(t('bookings.toast.approved', { number: res.data.number }), t('bookings.title')); },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.title'))
  });
  const decline = useMutation({
    mutationFn: (b: bookings.Booking) => bookings.declineBooking(b.id),
    onSuccess: (res) => { void refresh(); notifySuccess(t('bookings.toast.declined', { number: res.data.number }), t('bookings.title')); },
    onError: (e) => notifyError(bookingErrorText(t, e), t('bookings.title'))
  });

  useEffect(() => { /* keep the status filter in the address so a notice can link to requests */
    const next = new URLSearchParams(params);
    if (status) next.set('status', status); else next.delete('status');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  function openBooking(id: string | null) {
    const next = new URLSearchParams(params);
    if (id) next.set('booking', id); else next.delete('booking');
    setParams(next, { replace: !id });
  }

  function clearFilters() {
    setStatus('');
    setServiceId('');
    setStaffId('');
    setTo('');
    setSearch('');
  }

  const when = (b: bookings.Booking) => formatInZone(b.starts_at, tz, locale, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('bookings.list.title')}
        description={t('bookings.list.description')}
        withDivider={false}
        actions={(
          <Group gap="sm">
            <HelpButton guide="bookings" />
            <Button size="sm" onClick={() => setCreating(true)}>{t('bookings.new.title')}</Button>
          </Group>
        )}
      />

      <TableShell
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t('bookings.list.search')}
        labels={{
          table: t('table.view.table'),
          cards: t('table.view.cards'),
          list: t('table.view.list'),
          search: t('table.search'),
          perPage: t('table.perPage'),
          pageSizeOption: (n) => t('table.perPageOption', { n }),
          empty: t('table.noRecords'),
          range: (a, b, total) => t('table.range', { from: a, to: b, total })
        }}
        filters={(
          <Group gap="xs" wrap="wrap" align="flex-end" data-guide="bookings-list-filters">
            <Select
              size="sm"
              aria-label={t('common.status')}
              value={status}
              onChange={(e) => setStatus(e.currentTarget.value)}
              data={[{ value: '', label: t('bookings.list.anyStatus') }, ...bookings.BOOKING_STATUSES.map((s) => ({ value: s, label: statusLabel(t, s) }))]}
            />
            <Select
              size="sm"
              aria-label={t('bookings.field.service')}
              value={serviceId}
              onChange={(e) => setServiceId(e.currentTarget.value)}
              data={[{ value: '', label: t('bookings.list.anyService') }, ...services.map((s) => ({ value: s.id, label: s.name }))]}
            />
            <Select
              size="sm"
              aria-label={t('bookings.field.person')}
              value={staffId}
              onChange={(e) => setStaffId(e.currentTarget.value)}
              data={[{ value: '', label: t('bookings.calendar.everyone') }, ...staff.map((p) => ({ value: p.id, label: p.name }))]}
            />
            <TextInput size="sm" type="date" aria-label={t('bookings.list.from')} value={from} onChange={(e) => setFrom(e.currentTarget.value)} />
            <TextInput size="sm" type="date" aria-label={t('bookings.list.to')} value={to} onChange={(e) => setTo(e.currentTarget.value)} />
          </Group>
        )}
        loading={list.isPending}
        error={list.error ? (
          <Alert color="error" role="alert">
            <Group gap="sm" justify="space-between" wrap="wrap">
              <span>{bookingErrorText(t, list.error)}</span>
              <Button size="xs" variant="default" onClick={() => void list.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        ) : undefined}
        isEmpty={!list.isPending && rows.length === 0}
        empty={(
          <EmptyState
            title={filtered ? t('bookings.list.emptyFiltered') : t('bookings.list.empty')}
            description={filtered ? t('bookings.list.emptyFilteredHint') : t('bookings.list.emptyHint')}
            action={filtered
              ? <Button size="xs" variant="default" onClick={clearFilters}>{t('inbox.filters.clear')}</Button>
              : <Button size="xs" onClick={() => setCreating(true)}>{t('bookings.new.title')}</Button>}
          />
        )}
      >
        <DataTable<Indexed<bookings.Booking>>
          records={rows}
          loading={list.isFetching && !list.isFetchingNextPage}
          idAccessor="id"
          onRowClick={(b) => openBooking(b.id)}
          columns={[
            { accessor: 'number', title: t('bookings.field.number'), render: (b) => <Text role="bodySmall" fw={600}>{b.number}</Text> },
            { accessor: 'starts_at', title: t('bookings.field.when'), render: (b) => <Text role="bodySmall" title={b.starts_at}>{when(b)}</Text> },
            {
              accessor: 'contact',
              title: t('bookings.field.client'),
              render: (b) => (
                <Stack gap={0}>
                  <Text role="bodySmall">{b.contact.name || t('common.unknown')}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{b.contact.phone || b.contact.email || ''}</Text>
                </Stack>
              )
            },
            {
              accessor: 'service',
              title: t('bookings.field.service'),
              render: (b) => (
                <Group gap={6} wrap="nowrap">
                  <span className="rr-bookings__swatch" style={{ background: colourOf.get(b.service.id) ?? serviceColour(null) }} aria-hidden />
                  <Text role="bodySmall">{b.service.name}</Text>
                </Group>
              )
            },
            { accessor: 'staff', title: t('bookings.field.person'), render: (b) => <Text role="bodySmall">{b.staff?.name ?? t('common.none')}</Text> },
            { accessor: 'status', title: t('common.status'), render: (b) => <StatusBadge status={statusTone(b.status)}>{statusLabel(t, b.status)}</StatusBadge> },
            { accessor: 'channel', title: t('bookings.field.channel'), render: (b) => <Text role="caption">{t(`bookings.channel.${b.channel ?? 'console'}`, b.channel ?? '')}</Text> },
            {
              accessor: 'actions',
              title: t('common.actions'),
              render: (b) => b.status !== 'requested' ? null : (
                <Group gap="xs" wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="xs"
                    loading={approve.isPending && approve.variables?.id === b.id}
                    aria-label={t('bookings.list.approveFor', { number: b.number })}
                    onClick={() => approve.mutate(b)}
                  >
                    {t('bookings.action.approve')}
                  </Button>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    loading={decline.isPending && decline.variables?.id === b.id}
                    aria-label={t('bookings.list.declineFor', { number: b.number })}
                    onClick={() => decline.mutate(b)}
                  >
                    {t('bookings.action.decline')}
                  </Button>
                </Group>
              )
            }
          ]}
        />
        {list.hasNextPage && (
          <Group justify="center" style={{ paddingTop: 8 }}>
            <Button size="xs" variant="default" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {t('action.loadMore')}
            </Button>
          </Group>
        )}
      </TableShell>

      <BookingDrawer bookingId={openId} onClose={() => openBooking(null)} tz={tz} />
      <NewBookingModal opened={creating} onClose={() => setCreating(false)} tz={tz} />
    </Stack>
  );
}
